<template>
  <!--
    Sticky "on this page" bar. Shows the current section as you scroll
    past the first heading, and expands downward into the full list of the
    page's section headers (like a compact fullscreen nav) so you can jump
    around without scrolling back up. Driven by a plain [{ id, title }] list, so
    it works for both the Library section headers and an article's markdown
    body headings. Hidden on desktop, where the TOC sidebar / view toggle
    already covers this, unless `desktop` is set (the Library, which has no
    sidebar).
  -->
  <div
    v-if="sections.length"
    class="section-sticky"
    :class="{ 'section-sticky--visible': visible, 'section-sticky--desktop': desktop }"
  >
    <div v-show="expanded" class="section-sticky__backdrop" @click="close"></div>

    <div class="section-sticky__bar">
      <button
        type="button"
        class="section-sticky__toggle"
        :aria-expanded="expanded ? 'true' : 'false'"
        aria-controls="section-sticky-panel"
        @click="toggle"
      >
        <span class="section-sticky__label">{{ currentTitle }}</span>
        <span
          class="section-sticky__icon"
          :class="{ 'section-sticky__icon--open': expanded }"
          aria-hidden="true"
        ></span>
        <span class="section-sticky__sr">{{ expanded ? 'Hide' : 'Show' }} page sections</span>
      </button>

      <transition name="section-panel">
      <nav
        v-show="expanded"
        id="section-sticky-panel"
        class="section-sticky__panel"
        aria-label="On this page"
      >
        <ul class="section-sticky__list" :style="{ '--stagger-count': sections.length }">
          <li v-for="(s, index) in sections" :key="s.id" :style="{ '--stagger-index': index }">
            <button
              type="button"
              class="section-sticky__link"
              :class="{ 'section-sticky__link--active': s.id === currentId }"
              @click="go(s.id)"
            >
              {{ s.title }}
            </button>
          </li>
        </ul>
      </nav>
      </transition>
    </div>
  </div>
</template>

<script>
// Distance below the top of the viewport at which a heading is considered the
// "current" section. Roughly the height of the sticky bar so the active title
// flips as each heading tucks under the bar.
const THRESHOLD = 72;

export default {
  name: 'SectionStickyBar',
  props: {
    // Ordered list of { id, title }. `id` must match a DOM element id on the page.
    sections: {
      type: Array,
      default: () => [],
    },
    // Space left above a heading when jumping to it (accounts for the bar).
    scrollOffset: {
      type: Number,
      default: 72,
    },
    // Also show from 768 up. Articles leave this off, because their TOC
    // sidebar is already on screen there; the Library has no sidebar.
    desktop: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
      currentId: null,
      atTop: true,
      pastFirst: false,
      ticking: false,
    };
  },
  computed: {
    visible() {
      return this.sections.length > 0 && this.pastFirst && !this.atTop;
    },
    currentTitle() {
      const found = this.sections.find((s) => s.id === this.currentId);
      return (found && found.title) || (this.sections[0] && this.sections[0].title) || '';
    },
  },
  watch: {
    sections() {
      this.$nextTick(this.recompute);
    },
    $route() {
      this.expanded = false;
      this.$nextTick(this.recompute);
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.$nextTick(this.recompute);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  },
  methods: {
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.recompute();
        this.ticking = false;
      });
    },
    recompute() {
      const y = window.scrollY || window.pageYOffset || 0;
      this.atTop = y < 8;
      if (this.atTop && this.expanded) this.expanded = false;

      const first = this.sections
        .map((s) => document.getElementById(s.id))
        .find((el) => el);
      if (!first) {
        this.pastFirst = false;
        return;
      }

      const firstTop = first.getBoundingClientRect().top + y;
      this.pastFirst = y + THRESHOLD >= firstTop;

      // Current section = the last heading whose top has scrolled to/above the
      // threshold line. Headings are in document order, so once one drops below
      // the line every later one does too.
      let current = null;
      for (const s of this.sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= THRESHOLD) {
          current = s.id;
        } else {
          break;
        }
      }
      this.currentId = current || (first && first.id) || null;
    },
    toggle() {
      this.expanded = !this.expanded;
    },
    close() {
      this.expanded = false;
    },
    go(id) {
      this.expanded = false;
      const el = document.getElementById(id);
      if (!el) return;
      const y =
        el.getBoundingClientRect().top +
        (window.scrollY || window.pageYOffset || 0) -
        this.scrollOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', `#${id}`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/scss/_section-nav.scss';

// Mobile-only by default: articles already have the TOC sidebar on desktop, so
// the bar would repeat it. `--desktop` (the Library) keeps it from 768 up, with
// its text on the page's own side margins so it lines up with the content.
.section-sticky:not(.section-sticky--desktop) {
  @media only screen and (min-width: 768px) {
    display: none;
  }
}

.section-sticky--desktop {
  .section-sticky__toggle,
  .section-sticky__link {
    @media only screen and (min-width: 768px) {
      padding-inline: var(--spacing-lg);
    }
    @media only screen and (min-width: 1201px) {
      padding-inline: var(--spacing-xl);
    }
  }
}

.section-sticky__backdrop {
  position: fixed;
  inset: 0;
  z-index: 99988;
  background: rgba(0, 0, 0, 0.18);
}

.section-sticky__bar {
  position: fixed;
  inset-block-start: 0;
  inset-inline: 0;
  z-index: 99990;
  display: flex;
  flex-direction: column;
  // The page colour with no rule under it, like the header nav.
  background: var(--background);
  transform: translateY(-100%);
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.section-sticky--visible .section-sticky__bar {
  transform: translateY(0);
}

.section-sticky__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  inline-size: 100%;
  margin: 0;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: none;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  color: var(--foreground);
  text-align: start;
}

.section-sticky__label {
  min-inline-size: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  // Match the sticky HeaderNav link size (global `a` = --font-500) so the
  // context bar reads as the same class of chrome, just bigger than before.
  font-size: var(--font-500);
  --reading-fontWeight: var(--font-500-weight);
  font-weight: var(--fontWeight-medium);
}

// Plus to minus, the same icon as the Library's section accordions at their
// phone size (MyLibrary .section-accordion-icon), on every screen: 24px, bars
// 14/24 at 1.75px. On open the plus turns half a turn while its vertical bar
// shrinks away. Gradients rather than a mask, because a mask swap can't animate.
.section-sticky__icon {
  --bar: var(--line-drawn);
  flex-shrink: 0;
  --box: 2.4rem;
  /* Each bar starts on a whole pixel: centred, a 1px bar in a 24px box sat
     on a half pixel and smeared across two. */
  --bar-at: round(down, calc((var(--box) - var(--bar)) / 2), 1px);
  @supports not (width: round(1px, 1px)) {
    --bar-at: calc((var(--box) - var(--bar)) / 2);
  }
  inline-size: var(--box);
  block-size: var(--box);
  background:
    linear-gradient(currentColor, currentColor) var(--bar-at) center / var(--bar) 58.33% no-repeat,
    linear-gradient(currentColor, currentColor) center var(--bar-at) / 58.33% var(--bar) no-repeat;
  transition:
    transform 0.25s ease,
    background-size 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.section-sticky__icon--open {
  background-size:
    var(--bar) 0,
    58.33% var(--bar);
  transform: rotate(180deg);
}

.section-sticky__sr {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.section-sticky__panel {
  max-block-size: 70vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border-block-start: var(--border);
}

.section-sticky__list {
  list-style: none;
  margin: 0;
  padding: var(--spacing-xxs) 0 var(--spacing-sm);
}

/* Shared with the article sidebar (_section-nav.scss): full colour, no
   underline at rest, the wavy accent line on hover, a solid underline on the
   current section. */
.section-sticky__link {
  @include section-nav-link;
  display: block;
  max-inline-size: 100%;
  margin: 0;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: none;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--font-500);
  --reading-fontWeight: var(--font-500-weight);
  font-weight: var(--font-500-weight);
  text-align: start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-sticky__link--active {
  @include section-nav-link-active;
}

/* Opening: the panel unrolls down from the bar while its links slide in from
   the left, one after another. Closing runs it backwards: the links slide back
   out, last first, then the panel rolls up into the bar. */
.section-panel-enter-active {
  animation: section-panel-unroll var(--duration-base) var(--ease-standard) both;
}

.section-panel-leave-active {
  animation: section-panel-unroll var(--duration-fast) var(--ease-exit) calc(var(--duration-fast) + 20ms) reverse both;
}

.section-panel-enter-active li {
  animation: section-link-in var(--duration-base) var(--ease-standard) both;
  animation-delay: calc(60ms + var(--stagger-index, 0) * var(--stagger-step));
}

.section-panel-leave-active li {
  animation: section-link-in var(--duration-fast) var(--ease-exit) reverse both;
  animation-delay: calc((var(--stagger-count, 1) - 1 - var(--stagger-index, 0)) * 30ms);
}

@keyframes section-panel-unroll {
  0% {
    clip-path: inset(0 0 100% 0);
  }

  100% {
    clip-path: inset(0);
  }
}

@keyframes section-link-in {
  0% {
    opacity: 0;
    transform: translateX(-1.6rem);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .section-panel-enter-active,
  .section-panel-leave-active,
  .section-panel-enter-active li,
  .section-panel-leave-active li {
    animation: none;
  }
}
</style>
